import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Alert, IconButton, TextField, Tooltip,
  Button, Grid, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem
} from '@mui/material';
import { Home, Grade, Edit, Save, Cancel, Add, Delete, WarningAmber, AutoFixHigh, ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API = process.env.REACT_APP_API_URL;

const emptyNew = { code: '', meaning: '', minPercent: '', maxPercent: '', points: '', color: '#1565c0' };

const GradingScalePage = () => {
  const navigate  = useNavigate();
  const [searchParams] = useSearchParams();
  const [systems, setSystems] = useState([]);      // [{ system, category, bandCount }]
  const [system,  setSystem]  = useState(() => searchParams.get('system') || 'CBC');
  const [grades,  setGrades]  = useState([]);
  const [editing, setEditing] = useState(null); // code of row being edited
  const [form,    setForm]    = useState({});
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [regrading, setRegrading] = useState(false);
  const [newForm, setNewForm] = useState(emptyNew);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: addOpen, value: newForm, onClose: () => setAddOpen(false) });
  const [confirmDel, setConfirmDel] = useState(null);       // band pending delete

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const loadSystems = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/academics/grading-systems`, { headers });
      setSystems(data || []);
    } catch { /* non-fatal */ }
  }, []); // eslint-disable-line
  useEffect(() => { loadSystems(); }, [loadSystems]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/academics/grading-scale?system=${encodeURIComponent(system)}`, { headers });
      setGrades(data);
    } catch { setError('Failed to load grading scale'); }
  }, [system]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  // Always offer the two curriculum systems even before they have bands.
  const systemOptions = useMemo(() => {
    const names = new Set(systems.map(s => s.system));
    ['CBC', '8-4-4'].forEach(n => names.add(n));
    if (system) names.add(system);
    return [...names];
  }, [systems, system]);
  const currentCategory = systems.find(s => s.system === system)?.category || '';

  const startEdit = (g) => {
    setEditing(g.code);
    setForm({ meaning: g.meaning, minPercent: g.minPercent, maxPercent: g.maxPercent, color: g.color, points: g.points });
    setError('');
  };
  const cancelEdit = () => { setEditing(null); setForm({}); };

  const handleSave = async (code) => {
    setSaving(true); setError('');
    try {
      await axios.put(`${API}/api/academics/grading-scale/${code}?system=${encodeURIComponent(system)}`, { ...form, system }, { headers });
      setSuccess('Updated'); setEditing(null);
      load(); setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleCreate = async () => {
    if (!newForm.code.trim())    { setError('Code is required'); return; }
    if (!newForm.meaning.trim()) { setError('Meaning is required'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/api/academics/grading-scale`, {
        ...newForm,
        system,
        minPercent: parseFloat(newForm.minPercent) || 0,
        maxPercent: parseFloat(newForm.maxPercent) || 0,
        points:     parseFloat(newForm.points) || 0,
      }, { headers });
      setSuccess('Grade added'); setAddOpen(false); setNewForm(emptyNew);
      load(); loadSystems(); setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Add failed');
    } finally { setSaving(false); }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/academics/grading-scale/seed-defaults`, {}, { headers });
      setSuccess(data.inserted
        ? `Seeded ${data.inserted} default band${data.inserted !== 1 ? 's' : ''} (CBC + 8-4-4)`
        : 'Defaults already present — scale is up to date');
      load(); loadSystems(); setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Seeding failed');
    } finally { setSeeding(false); }
  };

  const handleRegradeAll = async () => {
    setRegrading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/academics/assessment-scores/regrade`, {}, { headers });
      setSuccess(`Regraded ${data.updated} of ${data.scanned} score${data.scanned !== 1 ? 's' : ''} school-wide`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Regrade failed');
    } finally { setRegrading(false); }
  };

  const handleDelete = async () => {
    const code = confirmDel.code; setConfirmDel(null);
    try {
      await axios.delete(`${API}/api/academics/grading-scale/${code}?system=${encodeURIComponent(system)}`, { headers });
      setSuccess('Deleted'); load(); loadSystems(); setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', mt: { xs: 2, sm: 4 }, mb: 4, px: { xs: 1, sm: 0 } }}>
      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <Box display="flex" alignItems="center" gap={1}>
                <Grade sx={{ color: '#1565c0' }} />
                <Typography variant="h5" color="primary">Grading Scale</Typography>
              </Box>
              <TextField
                select size="small" label="System"
                value={system}
                onChange={(e) => { setSystem(e.target.value); setEditing(null); setError(''); }}
                sx={{ minWidth: 210 }}
              >
                {systemOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              {currentCategory
                ? <Chip label={currentCategory} size="small" color="info" variant="outlined" />
                : null}
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button variant="outlined" size="small" startIcon={<ArrowBack />} onClick={() => navigate('/setups/grading-systems')}>
                Grading Systems
              </Button>
              <Button variant="contained" size="small" startIcon={<Add />}
                onClick={() => { setNewForm(emptyNew); setError(''); setAddOpen(true); }}>
                Add Grade
              </Button>
              <Tooltip title="Seed the standard CBC & 8-4-4 bands (won't overwrite existing grades)">
                <span>
                  <Button variant="outlined" size="small" startIcon={<AutoFixHigh />}
                    onClick={handleSeedDefaults} disabled={seeding}>
                    {seeding ? 'Seeding…' : 'Seed Defaults'}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Re-stamp every saved score to its subject's current grading system. Run after editing a system's bands so existing grades pick up the change.">
                <span>
                  <Button variant="outlined" color="secondary" size="small" startIcon={<Grade />}
                    onClick={handleRegradeAll} disabled={regrading}>
                    {regrading ? 'Regrading…' : 'Regrade Scores'}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Back to Academics">
                <IconButton onClick={() => navigate('/academics')}
                  sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          <Typography variant="body2" color="text.secondary" mb={2}>
            Grade bands for <strong>{system}</strong>{currentCategory ? ` (${currentCategory})` : ''}. Assign this
            system to subjects under <strong>Stage Subjects</strong>; scores in those subjects are graded on these
            boundaries. Rename a system or change its category on the <strong>Grading Systems</strong> page.
          </Typography>

          <Box sx={{ overflowX: 'auto', mx: { xs: -1, sm: 0 } }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Meaning</strong></TableCell>
                <TableCell align="center"><strong>Min %</strong></TableCell>
                <TableCell align="center"><strong>Max %</strong></TableCell>
                <TableCell align="center"><strong>Points</strong></TableCell>
                <TableCell align="center"><strong>Color</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grades.map(g => (
                <TableRow key={g.code} hover>
                  <TableCell>
                    <Chip label={g.code} size="small" sx={{ bgcolor: g.color, color: '#fff', fontWeight: 700 }} />
                  </TableCell>
                  {editing === g.code ? (
                    <>
                      <TableCell>
                        <TextField size="small" value={form.meaning} onChange={e => setForm(f => ({ ...f, meaning: e.target.value }))} sx={{ minWidth: 200 }} />
                      </TableCell>
                      <TableCell align="center">
                        <TextField size="small" type="number" value={form.minPercent} onChange={e => setForm(f => ({ ...f, minPercent: parseFloat(e.target.value) }))} sx={{ width: 70 }} inputProps={{ min: 0, max: 100 }} />
                      </TableCell>
                      <TableCell align="center">
                        <TextField size="small" type="number" value={form.maxPercent} onChange={e => setForm(f => ({ ...f, maxPercent: parseFloat(e.target.value) }))} sx={{ width: 70 }} inputProps={{ min: 0, max: 100 }} />
                      </TableCell>
                      <TableCell align="center">
                        <TextField size="small" type="number" value={form.points} onChange={e => setForm(f => ({ ...f, points: parseFloat(e.target.value) }))} sx={{ width: 70 }} inputProps={{ min: 0, max: 10 }} />
                      </TableCell>
                      <TableCell align="center">
                        <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} style={{ width: 40, height: 32, cursor: 'pointer', border: 'none', borderRadius: 4 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Save"><IconButton size="small" color="success" onClick={() => handleSave(g.code)} disabled={saving}><Save fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Cancel"><IconButton size="small" onClick={cancelEdit}><Cancel fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{g.meaning}</TableCell>
                      <TableCell align="center"><strong>{g.minPercent}%</strong></TableCell>
                      <TableCell align="center"><strong>{g.maxPercent}%</strong></TableCell>
                      <TableCell align="center">{g.points}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: g.color, display: 'inline-block', border: '2px solid #eee' }} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => startEdit(g)}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setConfirmDel(g)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
              {grades.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      No bands in <strong>{system}</strong> yet. Click <strong>Add Grade</strong> to define one, or
                      <strong> Seed Defaults</strong> for the standard CBC / 8-4-4 bands.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </Box>

          <Box mt={3} p={2} bgcolor="grey.50" borderRadius={2}>
            <Grid container spacing={2}>
              {grades.map(g => (
                <Grid item key={g.code}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: g.color }} />
                    <Typography variant="caption"><strong>{g.code}</strong> — {g.meaning} ({g.minPercent}–{g.maxPercent}%)</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* ── Add Grade dialog ── */}
      {discardDialog}
      <Dialog open={addOpen} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Grade — {system}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField label="Code *" value={newForm.code} fullWidth size="small"
                onChange={e => setNewForm(f => ({ ...f, code: e.target.value }))}
                inputProps={{ style: { textTransform: 'uppercase' } }} helperText="e.g. EE" />
            </Grid>
            <Grid item xs={8}>
              <TextField label="Meaning *" value={newForm.meaning} fullWidth size="small"
                onChange={e => setNewForm(f => ({ ...f, meaning: e.target.value }))}
                helperText="e.g. Exceeding Expectation" />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Min %" type="number" value={newForm.minPercent} fullWidth size="small"
                onChange={e => setNewForm(f => ({ ...f, minPercent: e.target.value }))}
                inputProps={{ min: 0, max: 100 }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Max %" type="number" value={newForm.maxPercent} fullWidth size="small"
                onChange={e => setNewForm(f => ({ ...f, maxPercent: e.target.value }))}
                inputProps={{ min: 0, max: 100 }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Points" type="number" value={newForm.points} fullWidth size="small"
                onChange={e => setNewForm(f => ({ ...f, points: e.target.value }))}
                inputProps={{ min: 0, max: 10 }} />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary">Color</Typography>
                <input type="color" value={newForm.color}
                  onChange={e => setNewForm(f => ({ ...f, color: e.target.value }))}
                  style={{ width: 44, height: 32, cursor: 'pointer', border: 'none', borderRadius: 4 }} />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? 'Adding…' : 'Add Grade'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm Delete band dialog ── */}
      <Dialog open={Boolean(confirmDel)} onClose={() => setConfirmDel(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete grade <strong>{confirmDel?.code} — {confirmDel?.meaning}</strong> from {system}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GradingScalePage;
